import React, { useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { actions as dialogActions } from "@Store/Reducers/dialog";
import { getRedirectDomain } from "@Utils/redirectUrl";

// Seconds the thank-you message stays up before redirecting.
export const COMPLETION_REDIRECT_DELAY_SECONDS = 5;

type CompletionRedirectDialogProps = {
    redirectUrl: string;
};

// assign, so Back returns to the finished survey.
const navigate = (redirectUrl: string) => window.location.assign(redirectUrl);

const CompletionRedirectDialogContent: React.FC<
    CompletionRedirectDialogProps
> = ({ redirectUrl }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<any>();
    const [secondsLeft, setSecondsLeft] = useState(
        COMPLETION_REDIRECT_DELAY_SECONDS
    );

    // Back from the redirect target can restore this page from the back/forward cache with
    // the dialog still open at "0s" and no way to close it; dismiss it instead.
    useEffect(() => {
        const handlePageShow = (event: PageTransitionEvent) => {
            if (event.persisted) {
                dispatch(dialogActions.hideDialog());
            }
        };

        window.addEventListener("pageshow", handlePageShow);
        return () => window.removeEventListener("pageshow", handlePageShow);
    }, [dispatch]);

    useEffect(() => {
        if (secondsLeft <= 0) {
            navigate(redirectUrl);
            return;
        }

        const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
        return () => clearTimeout(timer);
    }, [redirectUrl, secondsLeft]);

    return (
        <Box sx={{ py: 1 }} role="status" aria-live="polite">
            <Typography variant="h6" sx={{ mb: 1 }}>
                {t("invitation.completion_redirect_title")}
            </Typography>
            <Typography variant="body2">
                {t("invitation.completion_redirect_countdown", {
                    domain: getRedirectDomain(redirectUrl),
                    seconds: Math.max(secondsLeft, 0),
                })}
            </Typography>
        </Box>
    );
};

const CompletionRedirectDialogActions: React.FC<
    CompletionRedirectDialogProps
> = ({ redirectUrl }) => {
    const { t } = useTranslation();

    return (
        <Button
            onClick={() => navigate(redirectUrl)}
            variant="contained"
            size="small"
            color="secondary"
            type="button"
        >
            {t("invitation.completion_redirect_go_now")}
        </Button>
    );
};

export default {
    content: CompletionRedirectDialogContent,
    actions: CompletionRedirectDialogActions,
    title: "Redirecting",
    disableCancelButton: true,
    isGuestData: false,
};
